import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '../api/client';
import { Brand } from '../components/Brand';
import { landingPath } from '../components/Landing';
import { useAuth } from '../store/auth';

export default function SetPassword() {
  // The token rides in the URL fragment (#token=...) so it never leaves the
  // browser — no Referer, server or proxy logs (auth H5). No query fallback:
  // pre-H5 query links are invalid anyway (no jti nonce).
  const token =
    new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const setMe = useAuth((s) => s.setMe);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.setPassword(token, pw);
      const me = await api.me();
      setMe(me);
      nav(landingPath(me.role), { replace: true });
    } catch (err: unknown) {
      if (err instanceof HttpError && err.status === 400) {
        setError('This link is invalid or has expired. Ask your admin for a new one.');
      } else if (err instanceof HttpError) {
        setError(`Couldn't set your password: ${err.detail}`);
      } else {
        setError("Couldn't reach the server. Check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative grid min-h-screen place-items-center bg-ff-paper px-5 py-10">
      <div className="absolute inset-0 bg-grain opacity-35 pointer-events-none" aria-hidden="true" />
      <div className="relative w-full max-w-md animate-rise rounded-2xl border border-ff-line bg-white p-6 shadow-soft sm:p-9">
        <Brand className="mb-10" linked={false} />
        <div>
          <p className="ff-kicker mb-3">Finish setup</p>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tightish text-ff-ink">
            Set your password.
          </h1>
          <p className="mt-2 text-sm text-ff-mute">
            Choose a password to finish setting up your account.
          </p>
        </div>

        {!token ? (
          <div
            role="alert"
            className="mt-7 rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-4 py-3 text-sm text-ff-danger"
          >
            This set-password link is missing its token. Ask your admin for a new one.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-5" noValidate>
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-4 py-3 text-sm text-ff-danger"
              >
                {error}
              </div>
            )}
            <div>
              <label htmlFor="set-pw" className="ff-label">New password</label>
              <input
                id="set-pw"
                data-testid="set-pw"
                type="password"
                autoFocus
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="at least 10 characters"
                className="ff-input"
              />
            </div>
            <button
              type="submit"
              data-testid="set-pw-submit"
              disabled={loading || !pw}
              className="ff-btn-primary w-full py-3.5 text-base"
            >
              {loading ? 'Saving…' : 'Set password & sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
